import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { changePassword } from '@/features/auth/api/auth';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { passwordProblem } from '@/features/team/lib/password';
import { useT } from '@/lib/i18n';

import { markPasswordChanged } from '../lib/passwordChanged';

/**
 * Choose your own password.
 *
 * Rendered inside the auth card rather than the dashboard, because the caller who
 * needs it most cannot load the dashboard: when a password was set by somebody
 * else the API refuses every endpoint but this one, so a page that fetched
 * anything would show an error to somebody who has done nothing wrong.
 *
 * The "current password" field is what the API insists on, and rightly: it is what
 * stops a borrowed access token from being turned into a permanent credential.
 * Somebody handed a temporary password knows it, so it is no obstacle to them;
 * somebody who stole a session does not.
 */
export function ChangePasswordPage() {
  const { status, mustChangePassword, signOut } = useAuth();
  const navigate = useNavigate();
  const t = useT();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const problem = next.length > 0 ? passwordProblem(next) : undefined;
  const mismatch =
    confirmation.length > 0 && next !== confirmation ? t('auth.change.mismatch') : undefined;
  const ready = current.length > 0 && next.length > 0 && !problem && !mismatch;

  const submit = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: async () => {
      // The change ends every session, this one included, so there is nothing to
      // return to - signing in again is the honest next step.
      markPasswordChanged();
      await signOut();
      navigate('/login', { replace: true });
    },
  });

  if (status === 'anonymous') return <Navigate to="/login" replace />;

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-ink">
        {mustChangePassword ? t('auth.change.title_forced') : t('auth.change.title')}
      </h1>
      <p className="mt-1.5 text-sm text-muted">
        {mustChangePassword ? t('auth.change.intro_forced') : t('auth.change.intro')}
      </p>

      <form
        className="mt-6 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) submit.mutate();
        }}
      >
        <TextField
          label={mustChangePassword ? t('auth.change.given_password') : t('auth.change.current_password')}
          type="password"
          autoComplete="current-password"
          info={
            mustChangePassword
              ? t('tipsAccount.password.given_password')
              : t('tipsAccount.password.current_password')
          }
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <TextField
          label={t('auth.change.new_password')}
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          error={problem}
          hint={problem ? undefined : t('auth.common.min_length_hint', { min: 10 })}
          info={t('tipsAccount.password.new_password')}
        />
        <TextField
          label={t('auth.change.confirm_password')}
          type="password"
          autoComplete="new-password"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          error={mismatch}
          info={t('tipsAccount.password.confirm_password')}
        />

        {submit.isError ? (
          <Alert tone="error">
            {submit.error instanceof ApiError
              ? submit.error.message
              : t('auth.change.generic_error')}
          </Alert>
        ) : null}

        <div className="flex items-center gap-2 pt-1">
          <Button type="submit" disabled={!ready || submit.isPending}>
            {submit.isPending ? t('auth.common.saving') : t('auth.change.submit')}
          </Button>
          {mustChangePassword ? (
            // There is no way round it, so the only other door is out. Offered
            // because an account that can neither proceed nor leave is a trap.
            <Button type="button" variant="ghost" onClick={() => void signOut()}>
              {t('auth.change.sign_out')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              {t('auth.change.cancel')}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
